/**
 * The email block document model and its HTML serialiser.
 *
 * An email is an ordered list of typed blocks plus document settings. This
 * module is the only thing that turns that list into the `htmlContent` that
 * actually gets sent, so it is deliberately pure: no React, no DOM, no clock,
 * no network. Everything here is a plain function over plain data.
 *
 * `htmlContent` remains the sending source of truth. Blocks are an authoring
 * format layered on top; nothing downstream of the save reads them.
 */

// A block property that duplicates an inline declaration on the copy it wraps
// has to win over it — otherwise Align, Size, Line height, Colour and Font do
// nothing at all on a converted block, in the canvas AND in the sent email.
// See emailTypographyOverride.js; `previewOf` uses the same helper, so the two
// cannot disagree about what an override removes.
const { stripOverriddenDeclarations } = require('./emailTypographyOverride');

// --- the model ------------------------------------------------------------

// The palette, in the order it is offered to the user. `renderBlocks` and
// `createBlock` are both driven off this list, so a type that appears in the
// palette without a renderer or defaults fails the tests immediately.
const BLOCK_TYPES = [
  { type: 'heading', label: 'Heading' },
  { type: 'text', label: 'Text' },
  { type: 'button', label: 'Button' },
  { type: 'image', label: 'Image' },
  { type: 'social', label: 'Social row' },
  { type: 'divider', label: 'Divider' },
  { type: 'spacer', label: 'Spacer' },
];

const DEFAULT_SETTINGS = {
  width: 600, // px, the classic email body width
  background: '#efeae7', // outside the card
  cardBackground: '#ffffff',
  fontFamily: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
  // The body-copy metrics for the whole document. They were literals inside
  // `renderText` until the conversion work measured the corpus and found 172 of
  // 224 text cells carrying their own font-size/line-height/colour/family — so
  // a converted email could not keep its own type without them. The values are
  // exactly what `renderText` used to hard-code, which is what makes every
  // document saved before this change render byte-identically.
  fontSize: 16,
  lineHeight: 24,
  textColor: '#101b24',
  linkColor: '#4c72ff',
};

// A social row's icons, when one is added from the palette. These four are the
// set the PRO networking emails actually ship — same URLs, same brand chips —
// so a row added by hand and a row produced by the converter look alike.
const DEFAULT_SOCIAL_ICONS = [
  { src: 'https://www.mailjet.com/images/theme/v1/icons/ico-social/facebook.png', href: 'https://www.facebook.com/PrimaveraPro', alt: 'Facebook', width: 20, background: '#3b5998', radius: 3 },
  { src: 'https://www.mailjet.com/images/theme/v1/icons/ico-social/instagram.png', href: 'https://www.instagram.com/primaverapro', alt: 'Instagram', width: 20, background: '#3f729b', radius: 3 },
  { src: 'https://www.mailjet.com/images/theme/v1/icons/ico-social/youtube.png', href: 'https://www.youtube.com/user/PrimaveraSound', alt: 'YouTube', width: 20, background: '#EB3323', radius: 3 },
  { src: 'https://www.mailjet.com/images/theme/v1/icons/ico-social/linkedin.png', href: 'https://www.linkedin.com/showcase/primaver-pro/', alt: 'LinkedIn', width: 20, background: '#0077b5', radius: 3 },
];

const BLOCK_DEFAULTS = {
  // `lineHeight` and `fontFamily` are '' rather than a value: empty means "use
  // the document's own", which is what every heading saved before they existed
  // does. Same for the four typography props on `text`.
  heading: {
    text: 'Your heading', level: 2, align: 'left', color: '#101b24', fontSize: 28, lineHeight: '', fontFamily: '',
  },
  text: {
    html: '<p>Write something here.</p>', align: 'left', fontSize: '', lineHeight: '', color: '', fontFamily: '',
  },
  button: {
    label: 'Button',
    href: 'https://www.primaverasound.com',
    background: '#101b24',
    color: '#ffffff',
    radius: 4,
    align: 'center',
    paddingX: 28,
    paddingY: 14,
  },
  image: {
    src: 'https://s3-eu-west-1.amazonaws.com/public.primaverasound.com/psb/images/header-img.png',
    alt: '',
    href: '',
    width: 600,
    align: 'center',
  },
  social: { icons: DEFAULT_SOCIAL_ICONS, align: 'center', gap: 8 },
  divider: { color: '#e3ded9', thickness: 1, spacing: 16 },
  spacer: { height: 24 },
};

const EMPTY_DOC = Object.freeze({
  version: 1,
  settings: Object.freeze({ ...DEFAULT_SETTINGS }),
  blocks: Object.freeze([]),
});

// Ids never reach the rendered HTML — see `renderBlocks` — so they play no part
// in determinism. They do need to stay unique across sessions: a saved document
// comes back with ids from a previous page load, and a plain counter starting
// at 1 would collide with them and break react-beautiful-dnd's draggableId
// contract. Hence a per-module-load random prefix plus a counter.
const ID_PREFIX = `b${Math.random().toString(36).slice(2, 8)}`;
let idCounter = 0;

function nextId() {
  idCounter += 1;
  return `${ID_PREFIX}-${idCounter}`;
}

function createBlock(type) {
  const defaults = BLOCK_DEFAULTS[type];
  if (!defaults) throw new Error(`Unknown block type: ${type}`);
  const block = { id: nextId(), type, ...defaults };
  // A spread is shallow, so two social rows created from the palette would
  // otherwise share ONE icons array and editing either would edit both. Every
  // list-valued prop is copied one level deeper, entries included.
  Object.keys(block).forEach((key) => {
    if (Array.isArray(block[key])) {
      block[key] = block[key].map(entry => (entry && typeof entry === 'object' ? { ...entry } : entry));
    }
  });
  return block;
}

// --- escaping -------------------------------------------------------------

// Handlebars expressions are substituted at send time and appear in headings,
// button labels and hrefs all over this corpus. Escaping their innards would
// break every template that uses one, so they are lifted out of the text before
// escaping and put back verbatim. Triple-stash first: `{{{x}}}` must not match
// as `{{` + `{x}`.
const HANDLEBARS = /(\{\{\{[\s\S]*?\}\}\}|\{\{[\s\S]*?\}\})/g;

// `&`, `<`, `>` and `"` are the complete set: every attribute this serialiser
// emits is double-quoted, so an apostrophe needs no escaping — and escaping it
// would turn `font-family:Arial, 'Helvetica Neue'` into a style attribute full
// of `&#39;`, which the stricter email clients do not decode inside CSS.
function escapeSegment(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Escapes text and attribute values. Everything the user types that is not
// rich text goes through here — heading text, button labels, alt text, hrefs,
// colours — so nothing can close an attribute or open a tag.
function escapeText(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .split(HANDLEBARS)
    .map((part, index) => (index % 2 === 1 ? part : escapeSegment(part)))
    .join('');
}

// --- value coercion -------------------------------------------------------

const ALIGNMENTS = ['left', 'center', 'right'];

function align(value, fallback) {
  return ALIGNMENTS.indexOf(value) === -1 ? fallback : value;
}

function num(value, fallback) {
  // '' is the state of a number field the user has just cleared to retype it,
  // and `Number('')` is 0 — which for a clamped value means the minimum, so a
  // spacer would collapse to 1px mid-keystroke and a width would snap to 320.
  // An absent value means "use the default", never "use zero".
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : fallback;
}

function clamp(value, min, max, fallback) {
  const parsed = num(value, fallback);
  if (parsed < min) return min;
  if (parsed > max) return max;
  return parsed;
}

function settingsOf(doc) {
  const given = (doc && doc.settings) || {};
  // Built key by key from a literal, never by spreading user input, so the
  // output can never depend on the insertion order of a stored object.
  return {
    width: clamp(given.width, 320, 900, DEFAULT_SETTINGS.width),
    background: escapeText(given.background || DEFAULT_SETTINGS.background),
    cardBackground: escapeText(given.cardBackground || DEFAULT_SETTINGS.cardBackground),
    fontFamily: escapeText(given.fontFamily || DEFAULT_SETTINGS.fontFamily),
    fontSize: clamp(given.fontSize, 8, 96, DEFAULT_SETTINGS.fontSize),
    lineHeight: clamp(given.lineHeight, 8, 200, DEFAULT_SETTINGS.lineHeight),
    textColor: escapeText(given.textColor || DEFAULT_SETTINGS.textColor),
    linkColor: escapeText(given.linkColor || DEFAULT_SETTINGS.linkColor),
  };
}

// --- block renderers ------------------------------------------------------
//
// Every renderer returns the *contents* of one `<td>`; `renderBlocks` wraps
// each in its own `<tr><td>`. Padding is carried on that cell, never on a
// margin, because Outlook's word-processor rendering engine ignores margins on
// block elements.

const GUTTER = 24;

// A typography prop is either a value of its own or empty, and empty means "the
// document's". Written once here so `text` and `heading` cannot disagree about
// what an empty font-family means — and so a document saved before these props
// existed renders exactly as it did, because absent reads as empty.
function typeFace(block, settings) {
  const family = typeof block.fontFamily === 'string' ? block.fontFamily.trim() : '';
  return family ? escapeText(family) : settings.fontFamily;
}

function renderHeading(block, settings) {
  const level = clamp(block.level, 1, 3, 2);
  const size = clamp(block.fontSize, 8, 96, BLOCK_DEFAULTS.heading.fontSize);
  const style = [
    'margin:0',
    'padding:0',
    `font-family:${typeFace(block, settings)}`,
    `font-size:${size}px`,
    `line-height:${clamp(block.lineHeight, 8, 200, Math.round(size * 1.25))}px`,
    'font-weight:bold',
    `color:${escapeText(block.color || settings.textColor)}`,
    `text-align:${align(block.align, 'left')}`,
  ].join(';');
  return {
    cellStyle: `padding:16px ${GUTTER}px 8px ${GUTTER}px;`,
    html: `<h${level} style="${style};">${escapeText(block.text)}</h${level}>`,
  };
}

function renderText(block, settings) {
  const style = [
    `font-family:${typeFace(block, settings)}`,
    `font-size:${clamp(block.fontSize, 8, 96, settings.fontSize)}px`,
    `line-height:${clamp(block.lineHeight, 8, 200, settings.lineHeight)}px`,
    `color:${block.color ? escapeText(block.color) : settings.textColor}`,
    `text-align:${align(block.align, 'left')}`,
  ].join(';');
  // DELIBERATE EXCEPTION TO ESCAPING. `block.html` is rich text produced by the
  // Tiptap editor and is emitted verbatim — escaping it would render the user's
  // formatting as visible tags. This is the only unescaped value in the whole
  // serialiser; every other user-supplied string goes through `escapeText`.
  // It looks like a bug. It is not. Sanitisation is the editor's job.
  //
  // The one thing done TO it is removing the declarations the container above
  // duplicates: a converted paragraph carries `text-align`/`font-size`/`color`
  // of its own, the child wins in every client, and without this the five
  // controls in the properties panel are inert. It is a render-time edit only —
  // `block.html` in the document is never rewritten, so clearing a property
  // restores exactly what the paragraph had. An unset property strips nothing.
  const stored = block.html === null || block.html === undefined ? '' : String(block.html);
  const html = stripOverriddenDeclarations(stored, block);
  return {
    cellStyle: `padding:8px ${GUTTER}px;`,
    html: `<div style="${style};">${html}</div>`,
  };
}

function renderButton(block, settings) {
  const paddingY = clamp(block.paddingY, 0, 60, BLOCK_DEFAULTS.button.paddingY);
  const paddingX = clamp(block.paddingX, 0, 120, BLOCK_DEFAULTS.button.paddingX);
  const style = [
    'display:inline-block',
    `background-color:${escapeText(block.background || BLOCK_DEFAULTS.button.background)}`,
    `color:${escapeText(block.color || BLOCK_DEFAULTS.button.color)}`,
    `border-radius:${clamp(block.radius, 0, 60, BLOCK_DEFAULTS.button.radius)}px`,
    `padding:${paddingY}px ${paddingX}px`,
    'mso-padding-alt:0px', // Outlook applies padding to the anchor's box, not its text
    `font-family:${settings.fontFamily}`,
    'font-size:16px',
    'line-height:120%',
    'font-weight:bold',
    'text-decoration:none',
    'text-align:center',
  ].join(';');
  const alignment = align(block.align, 'center');
  return {
    cellStyle: `padding:16px ${GUTTER}px;text-align:${alignment};`,
    cellAlign: alignment,
    html: `<a href="${escapeText(block.href)}" target="_blank" style="${style};">${escapeText(block.label)}</a>`,
  };
}

// A block-level box with an explicit width does NOT respond to text-align on its
// parent — that only moves inline content. So an <img> at display:block sits hard
// left in the cell no matter what the cell says, and the alignment control does
// nothing in the sent email.
//
// It showed up as a 390px logo centred in the editor and flush left in the inbox.
// The editor drew it inside a plain <div> in a browser with a stylesheet; the email
// has nothing but these inline declarations.
//
// display:block stays — it is what kills the baseline gap under an image in most
// clients — so the centring has to come from the margins instead.
function imageMargin(alignment) {
  if (alignment === 'center') return 'margin-left:auto;margin-right:auto';
  if (alignment === 'right') return 'margin-left:auto;margin-right:0';
  return 'margin-left:0;margin-right:auto';
}

function renderImage(block, settings) {
  const width = clamp(block.width, 1, settings.width, settings.width);
  const style = [
    'border:0',
    'display:block',
    'outline:none',
    'text-decoration:none',
    `width:${width}px`,
    'max-width:100%',
    'height:auto',
    imageMargin(align(block.align, 'center')),
    '-ms-interpolation-mode:bicubic',
  ].join(';');
  // Only an image that is most of the card gets to go fluid on a phone. A 20px
  // social icon carrying width:100% would blow up to the full screen.
  const fluid = width >= settings.width * 0.5 ? ' class="em-fluid"' : '';
  const img = `<img${fluid} src="${escapeText(block.src)}" alt="${escapeText(block.alt)}" width="${width}" style="${style};" />`;
  const href = typeof block.href === 'string' ? block.href.trim() : '';
  const alignment = align(block.align, 'center');
  return {
    cellStyle: `padding:0;text-align:${alignment};`,
    cellAlign: alignment,
    html: href ? `<a href="${escapeText(block.href)}" target="_blank" style="text-decoration:none;">${img}</a>` : img,
  };
}

/**
 * The icons of a social row, filtered to the ones that can actually be drawn.
 *
 * An icon with no `src` is a half-filled form row, not a picture: rendering it
 * would put a broken-image box in the footer of every send. Dropping it here
 * (and only here) keeps the list the user is editing intact while the email
 * stays clean — and `validateDoc` says so out loud, so a blank row is reported
 * rather than silently disappearing.
 */
function socialIcons(block) {
  const icons = block && Array.isArray(block.icons) ? block.icons : [];
  return icons.filter(icon => icon && typeof icon.src === 'string' && icon.src.trim() !== '');
}

// The narrowest screen worth designing for (320px) minus the card's own 16px side
// padding. A social row wider than this pushes itself off the edge of the phone.
const MOBILE_CONTENT_WIDTH = 288;
// Below this an icon is a smudge; better to overflow slightly than to be illegible.
const MIN_ICON_WIDTH = 16;

const iconWidth = icon => clamp(icon.width, 1, 200, 20);

/*
Per-width mobile rules for every social row in the document.

A social row is the one block whose intrinsic width does not follow the card. The
card can go to 100% and each icon still occupies its fixed pixel width, so PS's
own footer — five 60px circles and a 100px wordmark, plus gaps — needs 448px and
simply hangs off the side of a 375px phone. That is exactly what the hand-written
original solved, with `.logo { width: 40px !important }` at 600px and 35px at
400px: tuned numbers, written by a person, thrown away by the conversion.

Rather than guess replacements, the scale is derived: take the widest social row
in the document, and shrink everything by whatever factor makes THAT row fit.
Every row scales by the same factor so the footer stays visually consistent, and
relative icon sizes are preserved — the wordmark stays wider than the circles.

Keyed by width rather than by position, so the five 60px icons collapse to one
rule, and a document with no social rows (or rows that already fit) emits nothing.
*/
function socialMobileRules(blocks) {
  const rows = (Array.isArray(blocks) ? blocks : []).filter(b => b && b.type === 'social');

  const widths = new Set();
  // The tightest row decides the scale for all of them. 1 means everything fits.
  let scale = 1;

  rows.forEach((block) => {
    const icons = socialIcons(block);
    if (icons.length === 0) return;
    const gap = clamp(block.gap, 0, 40, BLOCK_DEFAULTS.social.gap);

    let iconTotal = 0;
    icons.forEach((icon) => {
      const width = iconWidth(icon);
      widths.add(width);
      iconTotal += width;
    });
    // Each cell carries half the gap on both sides, so a whole gap per icon.
    const gapTotal = gap * icons.length;

    // Gaps do NOT scale — they are padding, not image width — so they come off
    // the budget before the ratio is taken. Scaling them along with the icons was
    // the first version of this and it undershot: the row still overflowed by
    // exactly the gap total.
    const budget = MOBILE_CONTENT_WIDTH - gapTotal;
    if (budget <= 0 || iconTotal === 0) return;
    scale = Math.min(scale, budget / iconTotal);
  });

  if (scale >= 1 || widths.size === 0) return [];

  return [...widths].sort((a, b) => a - b).map((width) => {
    // Floor, not round: rounding up can put the row back over the budget it was
    // just scaled to fit.
    const scaled = Math.max(MIN_ICON_WIDTH, Math.floor(width * scale));
    return `.em-si-${width} { width: ${scaled}px !important; height: auto !important; }`;
  });
}

/**
 * A horizontal row of linked icons — the shape every footer in this corpus ends
 * with, and the one thing the six-block palette could not express. Without it a
 * four-icon social bar converts into four full-width image blocks stacked
 * vertically, which is the single loudest failure the conversion probe found.
 *
 * One table, one `<tr>`, one `<td>` per icon. NOT MJML's four sibling tables
 * with `display:inline-table`: Word-based Outlook ignores `display` entirely and
 * lays those out as four stacked blocks, which is the very failure this block
 * exists to prevent. Each icon's coloured chip is a nested one-cell table,
 * because a background on an inline element is the other thing Outlook drops.
 *
 * Zero icons renders NOTHING — not an empty table, not a padded cell. An email
 * client given `<table><tr></tr></table>` still reserves the cell's padding, so
 * an empty row would show as an unexplained gap in the footer.
 */
function renderSocial(block) {
  const icons = socialIcons(block);
  if (icons.length === 0) return null;

  const alignment = align(block.align, 'center');
  const gap = clamp(block.gap, 0, 40, BLOCK_DEFAULTS.social.gap);
  const side = Math.round(gap / 2);

  const cells = icons.map((icon) => {
    const width = clamp(icon.width, 1, 200, 20);
    const radius = clamp(icon.radius, 0, 60, 0);
    const background = typeof icon.background === 'string' ? icon.background.trim() : '';
    const chipStyle = [
      'border-collapse:collapse',
      `width:${width}px`,
      background ? `background-color:${escapeText(background)}` : '',
      radius ? `border-radius:${radius}px` : '',
    ].filter(Boolean).join(';');
    // WIDTH ONLY. This used to emit height="${width}" and height:${width}px, on the
    // assumption that a social icon is square. Five of them are; the sixth is a
    // wordmark. PS's default newsletter ends with a 100px-wide "RADIO PRIMAVERA
    // SOUND" logotype, and forcing it to 100px tall stretched it into a smear.
    //
    // The hand-written original never set a height either — its CSS said
    // `.logo { width: 60px }` and `.radio-logo { width: 100px }` and let each image
    // keep its own aspect ratio. height:auto restores exactly that.
    const img = `<img class="em-si-${width}" src="${escapeText(icon.src)}" alt="${escapeText(icon.alt)}" width="${width}" style="border:0;display:block;outline:none;text-decoration:none;width:${width}px;height:auto;" />`;
    const href = typeof icon.href === 'string' ? icon.href.trim() : '';
    const linked = href
      ? `<a href="${escapeText(icon.href)}" target="_blank" style="text-decoration:none;">${img}</a>`
      : img;
    return [
      `<td style="padding:0 ${side}px;vertical-align:middle;">`,
      `<table class="em-si" role="presentation" border="0" cellpadding="0" cellspacing="0" width="${width}" style="${chipStyle};">`,
      '<tr>',
      // No height here either — a fixed cell height would reimpose the square the
      // image no longer has.
      `<td class="em-si" style="padding:0;font-size:0;line-height:0;width:${width}px;vertical-align:middle;">${linked}</td>`,
      '</tr>',
      '</table>',
      '</td>',
    ].join('');
  }).join('');

  return {
    cellStyle: `padding:8px ${GUTTER}px;text-align:${alignment};`,
    cellAlign: alignment,
    html: [
      `<table role="presentation" border="0" cellpadding="0" cellspacing="0" align="${alignment}" style="border-collapse:collapse;">`,
      '<tr>',
      cells,
      '</tr>',
      '</table>',
    ].join(''),
  };
}

function renderDivider(block) {
  const spacing = clamp(block.spacing, 0, 80, BLOCK_DEFAULTS.divider.spacing);
  const thickness = clamp(block.thickness, 1, 20, BLOCK_DEFAULTS.divider.thickness);
  const colour = escapeText(block.color || BLOCK_DEFAULTS.divider.color);
  // A one-cell table with a border-top: the only rule Outlook draws reliably.
  return {
    cellStyle: `padding:${spacing}px ${GUTTER}px;`,
    html: [
      '<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;">',
      '<tr>',
      `<td style="border-top:${thickness}px solid ${colour};font-size:0;line-height:0;height:0;">&nbsp;</td>`,
      '</tr>',
      '</table>',
    ].join(''),
  };
}

function renderSpacer(block) {
  const height = clamp(block.height, 1, 200, BLOCK_DEFAULTS.spacer.height);
  return {
    cellStyle: `padding:0;font-size:0;line-height:${height}px;height:${height}px;`,
    cellAttrs: ` height="${height}"`,
    html: '&nbsp;',
  };
}

const RENDERERS = {
  heading: renderHeading,
  text: renderText,
  button: renderButton,
  image: renderImage,
  social: renderSocial,
  divider: renderDivider,
  spacer: renderSpacer,
};

function renderRow(block, settings) {
  const renderer = block && RENDERERS[block.type];
  if (!renderer) return '';
  const part = renderer(block, settings);
  // A renderer may answer "there is nothing to draw" — a social row with no
  // icons is the case that exists. It gets no <tr> at all rather than an empty
  // one, because an empty row still costs its cell padding in every client.
  if (!part) return '';
  const alignAttr = part.cellAlign ? ` align="${part.cellAlign}"` : '';
  const extraAttrs = part.cellAttrs || '';
  // em-cell lets the phone breakpoint pull the 24px gutter in to 16px. An image
  // cell is padding:0 by design (a full-bleed hero) and must not gain any.
  const cellClass = part.cellStyle.indexOf('padding:0;') === 0 ? '' : ' class="em-cell"';
  return `        <tr>\n          <td${cellClass}${alignAttr}${extraAttrs} style="${part.cellStyle}">${part.html}</td>\n        </tr>`;
}

// --- the serialiser -------------------------------------------------------

/**
 * Turns a block document into a complete email HTML document.
 *
 * The shape below — nested tables, `width` attributes duplicated in inline
 * styles, an MSO conditional wrapper around the fixed-width card, a `<style>`
 * block that only resets client quirks — is dictated by Outlook's Word-based
 * rendering engine and by Gmail stripping `<head>` styles, not by taste. Do not
 * "modernise" it: there is no flexbox, no grid, no `<div>` column, and no
 * external stylesheet here on purpose. It mirrors the MJML output already
 * shipping in `ps-back/assets/**`, which is known to render correctly.
 *
 * Output is byte-identical for the same document: no clock, no ids in the
 * output, and every value read by explicit key rather than by iterating an
 * object, so nothing depends on insertion order.
 */
function renderBlocks(doc) {
  const settings = settingsOf(doc);
  const blocks = doc && Array.isArray(doc.blocks) ? doc.blocks : [];
  const rows = blocks.map(block => renderRow(block, settings)).filter(Boolean);
  // Computed from the blocks, not from the rendered rows: a social row's icons
  // keep their pixel widths however narrow the card gets, so they need their own
  // rules inside the breakpoint below. Empty for a document with no social row,
  // or one whose rows already fit a phone.
  const socialRules = socialMobileRules(blocks);
  const body = rows.length
    ? rows.join('\n')
    : '        <tr>\n          <td style="padding:0;font-size:0;line-height:1px;height:1px;">&nbsp;</td>\n        </tr>';

  return `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta name="x-apple-disable-message-reformatting" />
  <title></title>
  <style type="text/css">
    #outlook a { padding: 0; }
    body { margin: 0; padding: 0; width: 100% !important; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
    p { display: block; margin: 13px 0; }
    a { color: ${settings.linkColor}; }

    /* THE PHONE BREAKPOINT.
       Without this nothing adapts, and it is not enough to put max-width:100% on
       the card: in table layout a ${settings.width}px-wide image forces the table
       to that width, and the image's own max-width:100% then resolves against the
       table it just widened. The circle only breaks by overriding both, which is
       what !important inside a media query is for.
       Gmail, Apple Mail and every mobile webmail honour this. Outlook desktop
       ignores media queries entirely and keeps the fixed-width ghost table above,
       which is the behaviour we want there anyway. */
    @media only screen and (max-width: ${settings.width + 20}px) {
      .em-card { width: 100% !important; max-width: 100% !important; }
      .em-fluid { width: 100% !important; max-width: 100% !important; height: auto !important; }
      .em-cell { padding-left: 16px !important; padding-right: 16px !important; }${socialRules.length === 0 ? '' : `
      /* The chip table and cell around each icon carry the icon's desktop width.
         Released here so the .em-si-* rules below actually decide the size. */
      .em-si { width: auto !important; }
${socialRules.map(rule => `      ${rule}`).join('\n')}`}
    }
  </style>
  <!--[if mso]>
  <noscript><xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript>
  <![endif]-->
</head>
<body bgcolor="${settings.background}" style="margin:0;padding:0;background-color:${settings.background};word-spacing:normal;">
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" bgcolor="${settings.background}" style="width:100%;background-color:${settings.background};border-collapse:collapse;">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <!--[if mso | IE]><table role="presentation" align="center" border="0" cellpadding="0" cellspacing="0" width="${settings.width}" style="width:${settings.width}px;"><tr><td style="padding:0;"><![endif]-->
        <table class="em-card" role="presentation" align="center" border="0" cellpadding="0" cellspacing="0" width="${settings.width}" bgcolor="${settings.cardBackground}" style="width:${settings.width}px;max-width:100%;background-color:${settings.cardBackground};border-collapse:collapse;">
${body}
        </table>
        <!--[if mso | IE]></td></tr></table><![endif]-->
      </td>
    </tr>
  </table>
</body>
</html>
`;
}

// --- validation -----------------------------------------------------------

function isBlank(value) {
  return typeof value !== 'string' || value.trim() === '';
}

// Returns { valid, errors }. Every error names the offending block's id, so the
// editor can point at the block rather than saying "something is wrong".
function validateDoc(doc) {
  const errors = [];
  if (!doc || typeof doc !== 'object') return { valid: false, errors: ['The block document is missing.'] };
  if (!Array.isArray(doc.blocks)) return { valid: false, errors: ['The block document has no blocks array.'] };

  const seen = {};
  doc.blocks.forEach((block, index) => {
    const position = `block ${index + 1}`;
    if (!block || typeof block !== 'object') {
      errors.push(`${position} is not a block.`);
      return;
    }
    const id = typeof block.id === 'string' && block.id ? block.id : '';
    const name = id ? `Block ${id}` : `${position} (no id)`;
    if (!id) errors.push(`${position} has no id.`);
    else if (seen[id]) errors.push(`Block ${id} has a duplicate id.`);
    else seen[id] = true;

    if (isBlank(block.type)) {
      errors.push(`${name} has no type.`);
      return;
    }
    if (!RENDERERS[block.type]) {
      errors.push(`${name} has an unknown type "${block.type}".`);
      return;
    }
    if (block.type === 'button' && isBlank(block.href)) errors.push(`${name} is a button with an empty href.`);
    if (block.type === 'image' && isBlank(block.src)) errors.push(`${name} is an image with an empty src.`);
    if (block.type === 'text' && typeof block.html !== 'string') errors.push(`${name} is a text block with no html.`);
    if (block.type === 'social') {
      if (!Array.isArray(block.icons)) {
        errors.push(`${name} is a social row with no icons list.`);
      } else {
        // An icon with no src is dropped by the renderer, so it is invisible in
        // the proof and in the sent email alike. Named here rather than left to
        // be discovered by someone comparing the two.
        block.icons.forEach((icon, iconIndex) => {
          if (!icon || typeof icon !== 'object') {
            errors.push(`${name} has a social icon ${iconIndex + 1} that is not an icon.`);
          } else if (isBlank(icon.src)) {
            errors.push(`${name} has a social icon ${iconIndex + 1} with an empty src — it will not be sent.`);
          }
        });
      }
    }
  });

  return { valid: errors.length === 0, errors };
}

// --- list helpers ---------------------------------------------------------
//
// All four take a blocks array and return a new one. They never mutate the
// input, so they are safe against Apollo's frozen query results.

function asList(blocks) {
  return Array.isArray(blocks) ? blocks.slice() : [];
}

function inRange(index, length) {
  return Number.isInteger(index) && index >= 0 && index < length;
}

// A no-op for any out-of-range index: a bad drag must not corrupt the document.
function moveBlock(blocks, from, to) {
  const next = asList(blocks);
  if (!inRange(from, next.length) || !inRange(to, next.length) || from === to) return next;
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function insertBlock(blocks, block, index) {
  const next = asList(blocks);
  if (!block) return next;
  const at = Number.isInteger(index) ? Math.max(0, Math.min(index, next.length)) : next.length;
  next.splice(at, 0, block);
  return next;
}

function removeBlock(blocks, id) {
  return asList(blocks).filter(block => !block || block.id !== id);
}

function duplicateBlock(blocks, id) {
  const next = asList(blocks);
  const index = next.findIndex(block => block && block.id === id);
  if (index === -1) return next;
  next.splice(index + 1, 0, { ...next[index], id: nextId() });
  return next;
}

module.exports = {
  BLOCK_TYPES,
  BLOCK_DEFAULTS,
  DEFAULT_SETTINGS,
  EMPTY_DOC,
  // GUTTER, clamp and align are exported for the canvas miniatures only. The
  // editor draws each block as a preview of what `renderBlocks` will emit, and
  // a preview that clamps differently — or pads differently — from the
  // serialiser is a preview that lies. Sharing the three primitives is what
  // makes "what the canvas shows is what the proof shows" true by
  // construction rather than by two people remembering to keep numbers in
  // step. Nothing else should reach for them.
  GUTTER,
  clamp,
  align,
  // Which icons of a social row actually reach the email. Shared with the canvas
  // for the same reason as `clamp`: a preview that draws an icon the serialiser
  // drops is a preview that lies.
  socialIcons,
  createBlock,
  renderBlocks,
  validateDoc,
  moveBlock,
  insertBlock,
  removeBlock,
  duplicateBlock,
};
